<template>
<div class="container-fluid transfer two-to-four-year-transfer-container">
    <div class="row">
      <div class="col-md-12 text-center">
        <h2>{{ heading }}</h2>
      </div>
    </div>

    <div v-if="isLoading" class="row">
      <div class="col-md-12 text-center mt-5">
        <div class="loader"></div>
        <p>Loading plan...</p>
      </div>
    </div>

    <div v-else-if="errorMsg" class="row">
      <div class="col-md-12 text-center mt-5">
        <h4 style="color: red;">{{ errorMsg }}</h4>
      </div>
    </div>

    <template v-else-if="plan">
        <div v-if="isTwoToFourPlan" class="school-names">
          <span class="school-name set">
          <i class="fa-solid fa-school"></i>
          <span>{{ plan.startingSchool?.name }}</span>
        </span>
        <i class="fa-solid fa-arrow-right"></i>
        <span class="school-name set">
          <i class="fa-solid fa-school"></i>
          <span>{{ plan.destinationSchool?.name }}</span>
        </span>
      </div>

      <div class="row two-to-four-year-report-container">
        <div class="col-md-12 pl-0">
          <button
            @click="savePlan"
            class="btn btn-success mb-2 mr-2 ml-0"
            :disabled="isSaving"
          >
            <span v-if="isSaving">Saving...</span>
            <span v-else-if="saveSuccess">Changes Saved</span>
            <span v-else>Save Changes</span>
          </button>

          <button
            data-toggle="modal"
            data-target="#reportSummarModal"
            class="btn btn-primary mb-2"
            type="button"
          >
            Analyze transfer plan
          </button>
        </div>

        <div class="col-md-12 dp-visualization-container p-4">
          <DegreePlanVisualization
            v-if="planTermData.length"
            :planTermData="planTermData"
            :isTransferPlan="isTwoToFourPlan"
            :hadTransfer="isTwoToFourPlan"
            :readOnly="false"
            :route-path="isTwoToFourPlan ? '/TwoYeartoFourYear' : '/transfer'"
          />
        </div>
      </div>

      <ReportSummaryModal :reportData="plan.transferPlanAnalysis || {}" />
    </template>

    <div v-else class="row">
      <div class="col-md-12 text-center mt-5">
        <h4>Plan not found.</h4>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import ReportSummaryModal from "@/components/TransferSummaryModal.vue";
// @ts-ignore
import { DegreePlanVisualization } from "@unm-idi/vue-degree-plan/vue-degree-plan.js";
import store from "@/store";
import { cleanPlanForSave, resolveS3SlugFromPlan } from "@/utils/planSerialization";

// @ts-ignore
import "@unm-idi/vue-degree-plan/vue-degree-plan.css";

export default defineComponent({
  name: "PlanDetails",
  setup() {
    const route = useRoute();
    const planName = decodeURIComponent(String(route.params.planName || ""));
    const plan = ref<any>(null);
    const planTermData = ref<any[]>([]);
    const isLoading = ref(true);
    const errorMsg = ref("");
    const isSaving = ref(false);
    const saveSuccess = ref(false);
    const isTwoToFourPlan = computed(() => {
      return route.query.planType === "two-to-four";
    });

    const heading = computed(() =>
      plan.value?.planType === "completion"
        ? "Transfer evaluation plan"
        : "Two year to four year transfer plan"
    );
function applySchoolMeta(obj: any, role: string, label: string) {
  if (!obj || typeof obj !== "object") return;

  // snake_case
  obj.school_role = role;
  obj.school_label = label;

  // camelCase, in case vue-degree-plan reads this format
  obj.schoolRole = role;
  obj.schoolLabel = label;

  // extra common names, harmless if unused
  obj.institution_role = role;
  obj.institution_label = label;
  obj.institutionRole = role;
  obj.institutionLabel = label;
}

function restoreSchoolRoles(terms: any[], savedPlan: any) {
  const sourceName = savedPlan?.startingSchool?.name || "Pima Community College";
  const destName = savedPlan?.destinationSchool?.name || "University of Arizona";

  return (terms || []).map((t: any, index: number) => {
    const term = JSON.parse(JSON.stringify(t));

    const role =
      term.school_role ||
      term.schoolRole ||
      term.institution_role ||
      term.institutionRole ||
      (index < 4 ? "source" : "target");

    const normalizedRole =
      String(role).toLowerCase() === "target" ||
      String(role).toLowerCase() === "destination"
        ? "target"
        : "source";

    const label = normalizedRole === "source" ? sourceName : destName;

    applySchoolMeta(term, normalizedRole, label);

    if (Array.isArray(term.plan_items)) {
      term.plan_items.forEach((pi: any) => {
        applySchoolMeta(pi, normalizedRole, label);
        applySchoolMeta(pi?.requirement, normalizedRole, label);
        applySchoolMeta(pi?.requirement?.course, normalizedRole, label);
        applySchoolMeta(pi?.requirement?.course?.academic_period, normalizedRole, label);

        if (pi?.requirement) {
          pi.requirement.parent_id = term.id ?? index;
        }

        if (pi?.requirement?.course?.academic_period) {
          pi.requirement.course.academic_period.id = term.id ?? index;
          pi.requirement.course.academic_period.term = term.name || `Term ${index + 1}`;
          pi.requirement.course.academic_period.name = term.name || `Term ${index + 1}`;
        }
      });
    }

    return term;
  });
}
function stripSchoolMeta(value: any): any {
  const schoolMetaKeys = [
    "school_role",
    "school_label",
    "schoolRole",
    "schoolLabel",
    "institution_role",
    "institution_label",
    "institutionRole",
    "institutionLabel",
  ];

  if (Array.isArray(value)) {
    return value.map(stripSchoolMeta);
  }

  if (value && typeof value === "object") {
    const copy: any = {};

    Object.keys(value).forEach((key) => {
      if (schoolMetaKeys.includes(key)) return;
      copy[key] = stripSchoolMeta(value[key]);
    });

    return copy;
  }

  return value;
}
    onMounted(async () => {
      try {
          plan.value = await store.methods.fetchPlanDetail(planName);
          const rawTerms = plan.value.fourYearPlan || [];

          if (isTwoToFourPlan.value) {
            planTermData.value = restoreSchoolRoles(rawTerms, plan.value);
          } else {
            planTermData.value = stripSchoolMeta(rawTerms);
          }
      } catch (e: any) {
        console.error("Error loading plan:", e);
        errorMsg.value = e?.response?.data?.message || e.message || "Failed to load plan.";
      } finally {
        isLoading.value = false;
      }
    });

    async function savePlan() {
      if (!plan.value) return;
      try {
        isSaving.value = true;
        saveSuccess.value = false;

        const slugs = resolveS3SlugFromPlan(plan.value);

        const updatedPlan = {
          ...plan.value,
          s3Slug: slugs,
          fourYearPlan: cleanPlanForSave(planTermData.value),
        };

        await store.methods.uploadUserFile(
          updatedPlan,
          slugs.dest,
          slugs.start,
          updatedPlan.name,
          true
        );

        saveSuccess.value = true;
        setTimeout(() => { saveSuccess.value = false; }, 3000);
      } catch (e: any) {
        console.error("Error saving plan:", e);
        errorMsg.value = e?.response?.data?.message || e.message || "Failed to save plan.";
      } finally {
        isSaving.value = false;
      }
    }

      return {
        plan,
        planTermData,
        isLoading,
        errorMsg,
        isSaving,
        saveSuccess,
        savePlan,
        heading,
        isTwoToFourPlan,
      };
  },
  components: {
    DegreePlanVisualization,
    ReportSummaryModal,
  },
});
</script>

